import { createLogger } from "@/lib/logger";
import { useEffect, useRef, useState } from "react";

const logger = createLogger("useVoiceNoteAudioLevel");
const DETECTABLE_AUDIO_RMS = 260 / 32768;

export function getVoiceNoteAudioLevel(samples: Uint8Array): number {
  if (!samples.length) return 0;
  const rms = Math.sqrt(
    samples.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0) / samples.length
  );
  return Math.min(1, Math.max(0, (rms - 0.012) / 0.16));
}

/** Observes only the recorder's existing stream. No samples leave this hook. */
export function useVoiceNoteAudioLevel(stream: MediaStream | null) {
  const [audioLevel, setAudioLevel] = useState(0);
  const [showQuietWarning, setShowQuietWarning] = useState(false);
  const evidence = useRef({ available: false, monitoring: false, detectedAudio: false });
  useEffect(() => {
    setAudioLevel(0);
    setShowQuietWarning(false);
    evidence.current.monitoring = false;
    if (!stream) return;
    evidence.current = { available: false, monitoring: false, detectedAudio: false };
    const AudioContextClass =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let animationFrame = 0;
    let disposed = false;
    try {
      context = new AudioContextClass();
      source = context.createMediaStreamSource(stream);
      analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      if (context.state === "suspended") void context.resume().catch(() => undefined);
      const samples = new Float32Array(analyser.fftSize);
      let previousTime: number | null = null;
      let monitoredMs = 0;
      let smoothed = 0;
      const sample = (now: number) => {
        if (disposed || !analyser || !context) return;
        const monitoring = context.state === "running" && document.visibilityState !== "hidden";
        evidence.current.monitoring = monitoring;
        if (monitoring) {
          // Suspended/background intervals are not evidence of a silent microphone.
          if (previousTime !== null) monitoredMs += Math.min(100, Math.max(0, now - previousTime));
          try {
            analyser.getFloatTimeDomainData(samples);
          } catch {
            evidence.current.monitoring = false;
            setShowQuietWarning(false);
            logger.warn("Voice-note audio monitoring unavailable");
            return;
          }
          evidence.current.available = true;
          const rms = Math.sqrt(
            samples.reduce((sum, value) => sum + value * value, 0) / samples.length
          );
          if (rms >= DETECTABLE_AUDIO_RMS) evidence.current.detectedAudio = true;
          const target = Math.min(1, Math.max(0, (rms - 0.012) / 0.16));
          smoothed += (target - smoothed) * (target > smoothed ? 0.38 : 0.18);
          const level = smoothed < 0.012 ? 0 : smoothed;
          setAudioLevel((current) => (Math.abs(current - level) >= 0.012 ? level : current));
          setShowQuietWarning(monitoredMs >= 5000 && !evidence.current.detectedAudio);
        } else {
          setShowQuietWarning(false);
        }
        previousTime = monitoring ? now : null;
        animationFrame = window.requestAnimationFrame(sample);
      };
      animationFrame = window.requestAnimationFrame(sample);
    } catch {
      logger.warn("Voice-note audio monitoring unavailable");
    }
    return () => {
      disposed = true;
      window.cancelAnimationFrame(animationFrame);
      source?.disconnect();
      analyser?.disconnect();
      void context?.close().catch(() => undefined);
    };
  }, [stream]);
  return { audioLevel, showQuietWarning, evidence };
}
